import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import {
  TransactionFormRule,
  TransactionFormSchema,
} from "@factory/form/form.rule";
import { useFormDefaultTransaction } from "@factory/form/form-default-transaction.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateTransaction,
  useQyGetTransactionById,
  useQyDeleteTransaction,
  useQyCompleteTransaction,
  useQyCancelTransaction,
  useQyPayTransaction,
} from "@factory/query/transaction.query";
import {
  GetResponseTransactionModel,
  QueryKeyTransaction,
} from "@factory/model/query-model-transaction";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useCallback, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { GetServiceV2Dto } from "@api/api";
import {
  GetTransactionServiceModel,
  QueryKeyTransactionService,
} from "@factory/model/query-model-transaction-service";
import { useQueryClient } from "react-query";
import { TransactionQrModel } from "../transaction-qr/transaction-qr";
import { useQyCheckoutTransactionService } from "@factory/query/transaction-service.query";
import { useQyGetTransactionWaiver } from "@factory/query/transaction-waiver.query";
import { GetResponseTransactionWaiverModel } from "@factory/model/query-model-transaction-waiver";
import { GetWaiverModel } from "@factory/model/query-model-waiver";
import { viewAsset } from "@factory/query/google-cloud-storage.query";
import { useUserIdentityContext } from "@core/services/user-identity.context";
import { LocalRole } from "@core/model/local-role.enum";

export function useEditTransaction() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const userRole = getUserRole();
  const queryClient = useQueryClient();
  let [searchParams] = useSearchParams();
  const printWaiverRef = useRef(null);
  const printQrRef = useRef(null);
  const { getDefault } = useFormDefaultTransaction();
  const navigate = useNavigate();
  const { mapUpdateTransaction, mapDeleteTransaction } = useFormApiMapper();
  const { showError, showSuccess, showInfo } = useNotificationContext();
  const { transactionId } = useParams();
  const [qrData, setQrData] = useState<TransactionQrModel | null>(null);
  const [servicePanel, setServicePanel] = useState(false);
  const [servicePanelData, setServicePanelData] = useState<{
    model: GetServiceV2Dto;
    service: GetTransactionServiceModel;
  } | null>(null);

  const [selectedWaiverPreview, setSelectedWaiverPreview] =
    useState<GetWaiverModel | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const actionParam = searchParams.get("action");
  const memberTypeParam = searchParams.get("memberType");
  const codeParam = searchParams.get("code");
  const hasCalled = useRef(false);

  // QUERY DECLARATION
  // API GETBYID Transaction
  const handleGetApiSuccess = (data: GetResponseTransactionModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];
    setValue("isPaid", Boolean(responseData?.is_paid));
  };
  const {
    data: transactionResponse,
    isLoading,
    isError: transactionError,
  } = useQyGetTransactionById(transactionId || "", handleGetApiSuccess);
  const transactionData = transactionResponse?.data?.[0];
  const parentQr = `${transactionData?.parent_code}:parent:${transactionData?.code}`;

  const successTransactionWaiver = async (
    response: GetResponseTransactionWaiverModel
  ) => {
    if (!response || response.data?.length === 0) {
      return;
    }

    const responseData = response.data?.[0];
    const waiver = {
      code: responseData?.code,
      description: (responseData as any)?.description,
    } as GetWaiverModel;
    setSelectedWaiverPreview(waiver);

    if (responseData?.signature) {
      try {
        const file = await viewAsset(responseData.signature);
        const fileBlob = URL.createObjectURL(file);
        setSignaturePreview(fileBlob);

        const signatureFile = new File([file], "signature.png", {
          type: "image/png",
        });
      } catch (error) {
        console.error("Failed to load signature:", error);
      }
    }
  };
  useQyGetTransactionWaiver(
    "",
    1,
    0,
    undefined,
    { batch: transactionId ?? "" },
    !!transactionId,
    successTransactionWaiver
  );

  // API UPDATE Transaction
  const handleUpdateApiSuccess = () => {
    showSuccess("Transaction updated successfully");
  };
  const { mutate: updateTransaction, isLoading: isEditLoading } =
    useQyUpdateTransaction(handleUpdateApiSuccess);

  // API DELETE Transaction
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Transaction deleted successfully");
  };
  const { mutate: deleteTransaction } = useQyDeleteTransaction(
    handleDeleteApiSuccess
  );

  // API COMPLETE Transaction
  const handleCompleteApiSuccess = () => {
    showSuccess("Transaction paid and completed successfully");
  };
  const { mutate: completeTransaction } = useQyCompleteTransaction(
    handleCompleteApiSuccess
  );

  // API COMPLETE Transaction
  const handleCompletePayApiSuccess = () => {
    showSuccess("Transaction paid successfully");
  };
  const { mutate: payTransaction } = useQyPayTransaction(
    handleCompletePayApiSuccess
  );

  // API CANCEL Transaction
  const handleCancelApiSuccess = () => {
    showSuccess("Transaction paid and completed successfully");
  };
  const { mutate: cancelTransaction } = useQyCancelTransaction(
    handleCancelApiSuccess
  );

  // API COMPLETE Transaction
  const handleCheckoutApiSuccess = () => {
    queryClient.invalidateQueries([QueryKeyTransactionService, codeParam]);
    showSuccess("Child is checkout successfully");
  };
  const { mutate: checkoutTransaction } = useQyCheckoutTransactionService(
    handleCheckoutApiSuccess
  );

  useEffect(() => {
    if (actionParam !== "complete" || hasCalled.current) {
      return;
    }

    if (memberTypeParam === "parent") {
      handleAction({ value: ActionEnum.CompleteScan } as any);
    } else if (memberTypeParam === "child") {
      handleAction({ value: ActionEnum.Checkout } as any);
    }
    hasCalled.current = true;
  }, [actionParam]);

  // LOCAL FUNCTIONS
  const formMethod = useForm<TransactionFormSchema>({
    defaultValues: getDefault(transactionData),
    resolver: zodResolver(TransactionFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: TransactionFormSchema) => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data");
    }

    const formData = mapUpdateTransaction(form, transactionData.code);
    updateTransaction(formData);
  };
  const handleValidateError = (err: FieldErrors<TransactionFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data found");
    }

    const form = mapDeleteTransaction(transactionData.code);
    deleteTransaction(form);
  };
  const cancelConfirm = () => {
    if (!transactionData || !transactionData.code) {
      throw new Error("No transaction data found");
    }

    const form = mapDeleteTransaction(transactionData.code);
    cancelTransaction(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Checkout:
        if (!codeParam) {
          console.error(new Error("No query code found"));
          return;
        }

        const checkoutForm = mapDeleteTransaction(codeParam);
        checkoutTransaction(checkoutForm);
        break;

      case ActionEnum.Pay:
        if (!transactionData || !transactionData.code) {
          console.error(new Error("No transaction data found"));
          return;
        }

        confirmDialog({
          message: "Are you sure you want mark this transaction as paid?",
          header: "Pay Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-success",
          accept() {
            const form = mapDeleteTransaction(transactionData.code);
            payTransaction(form);
          },
        });
        break;

      case ActionEnum.CompleteScan:
        if (!transactionData || !transactionData?.code) {
          console.error(new Error("No transaction data found"));
          return;
        }

        const completeTransactionForm = mapDeleteTransaction(
          transactionData.code
        );
        completeTransaction(completeTransactionForm);
        break;

      case ActionEnum.Complete:
        if (!transactionData || !transactionData.code) {
          console.error(new Error("No transaction data found"));
          return;
        }

        confirmDialog({
          message:
            "Are you sure you want to mark paid and complete this transaction?",
          header: "Complete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-success",
          accept() {
            const form = mapDeleteTransaction(transactionData.code);
            completeTransaction(form);
          },
        });
        break;

      case ActionEnum.ScanComplete:
        navigate(`scan`);
        break;

      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;

      case ActionEnum.Cancel:
        confirmDialog({
          message: "Are you sure you want to cancel this transaction?",
          header: "Cancel Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept: cancelConfirm,
        });
        break;

      case ActionEnum.PrintWaiver:
        handlePrintWaiver();
        break;

      case ActionEnum.PrintQr:
        handlePrintQr();
        break;
    }
  };
  const handlePrintWaiver = useReactToPrint({ contentRef: printWaiverRef });
  const handlePrintQr = useReactToPrint({ contentRef: printQrRef });

  const handleServiceChange = (model: GetServiceV2Dto) => {
    const { code, member, service } = model;
    const serviceModel = {
      code,
      member,
      service,
    } as GetTransactionServiceModel;
    setServicePanel(true);
    setServicePanelData({
      model,
      service: serviceModel,
    });
  };
  const handleServiceChangeSuucess = () => {
    queryClient.invalidateQueries(QueryKeyTransaction);
    setServicePanel(false);
  };
  const handleQrData = (code: string, name: string, isParent: boolean) => {
    showInfo("Getting print data. Please wait!");
    setQrData({
      transactionCode: transactionData?.code || "",
      code: code,
      isParent,
      name,
    });
    setTimeout(() => {
      handlePrintQr();
    }, 100);
  };
  const getActions = useCallback(() => {
    const actions: ButtonAction[] = [
      { label: "Scan QR", type: "secondary", value: ActionEnum.ScanComplete },
      { label: "Set Paid", type: "primary", value: ActionEnum.Pay },
      { label: "Finish Session", type: "split", value: ActionEnum.Complete },
    ];
    if (userRole?.name.toUpperCase() === LocalRole.Manager) {
      actions.push({
        label: "Cancel Session",
        type: "more",
        value: ActionEnum.Cancel,
      });
    }

    return actions;
  }, [userRole.name]);

  return {
    printQrRef,
    printWaiverRef,
    transactionId,
    transactionData,
    isLoading,
    transactionError,
    formMethod,
    isEditLoading,
    servicePanel,
    servicePanelData,
    qrData,
    selectedWaiverPreview,
    signaturePreview,
    parentQr,
    getActions,
    handleQrData,
    setServicePanel,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
    handleServiceChange,
    handleServiceChangeSuucess,
    navigate,
    checkoutTransaction,
    mapDeleteTransaction,
  };
}
